import { ImportQuestion } from './types'

const L = ['A', 'B', 'C', 'D', 'E', 'F']
const CYR: Record<string, string> = { А: 'A', В: 'B', С: 'C', Д: 'D', Е: 'E', Б: 'B', Ә: 'E', Ф: 'F' }
const lat = (c: string) => (CYR[c.toUpperCase()] || c).toUpperCase()
const isMatch = (t: string) => /сәйкестендір|сәйкестенді|соотнес|сопостав|соответстви/i.test(t)
const ANS = /^(?:Жауабы|Жауап|Дұрыс жауабы?|Ответ|Answer)\s*[:\-–—]?\s*(.+)$/i
const ROW = /^([*+✓])?\s*([A-FА-ЕӘФ])\s*[).]\s*(.+?)\s*([*+✓])?$/i

interface Row {
    k: string | null
    t: string
    num?: boolean
    mark?: boolean
}
interface Block {
    text: string
    rows: Row[]
    ans: string
    image: boolean
    match: boolean
}

// Разбор вставленного из Word текста по правилам (без ИИ). Пустой результат -> вызывающий отдаёт текст ИИ.
export function parseText(raw: string): Omit<ImportQuestion, 'key'>[] {
    const blocks: Block[] = []
    let b: Block | null = null
    const start = (t: string) => {
        b = { text: t, rows: [], ans: '', image: /сурет|\[img|рисун/i.test(t), match: isMatch(t) }
        blocks.push(b)
    }
    for (let line of raw.replace(/\r/g, '').split('\n')) {
        line = line.replace(/ /g, ' ').replace(/\t+/g, ' ').trim()
        if (!line) continue
        let m: RegExpMatchArray | null
        if (b && (m = line.match(ANS))) {
            b.ans = m[1]
            continue
        }
        if ((m = line.match(/^(\d+)\s*([.)])\s*(.+)$/))) {
            if (b && b.match && !b.ans && m[2] === ')') {
                b.rows.push({ k: m[1], t: m[3].trim(), num: true })
                continue
            }
            start(m[3])
            continue
        }
        if (!b) {
            start(line)
            continue
        }
        if (/^[[(]?\s*(сурет|рисунок|img|image)/i.test(line)) {
            b.image = true
            continue
        }
        if ((m = line.match(ROW))) {
            b.rows.push({ k: lat(m[2]), t: m[3].trim(), mark: !!(m[1] || m[4]) })
            continue
        }
        if (!b.rows.length) {
            b.text += ' ' + line
            if (isMatch(b.text)) b.match = true
            continue
        }
        if (b.match) {
            b.rows.push({ k: null, t: line })
            continue
        }
        b.rows[b.rows.length - 1].t += ' ' + line
    }

    const uniq = (arr: string[]) => {
        const seen = new Set<string>()
        return arr.filter((t) => {
            const k = t.toLowerCase().replace(/\s+/g, ' ')
            if (seen.has(k)) return false
            seen.add(k)
            return true
        })
    }

    return blocks
        .map((q): Omit<ImportQuestion, 'key'> => {
            if (q.match) {
                const lab = q.rows.filter((r) => r.k)
                const plain = q.rows.filter((r) => !r.k)
                const nums = q.rows.filter((r) => r.num)
                let subs: Row[]
                let optT: string[]
                if (plain.length) {
                    subs = lab.slice(0, 2)
                    optT = plain.map((r) => r.t)
                } else if (nums.length) {
                    subs = nums.slice(0, 2)
                    optT = q.rows.filter((r) => r.k && !r.num).map((r) => r.t)
                } else {
                    subs = lab.slice(0, 2)
                    optT = lab.slice(2).map((r) => r.t)
                }
                const opts = uniq(optT).slice(0, 4).map((t, j) => [L[j], t] as [string, string])
                const sk = subs.map((r) => r.k)
                const correct: Record<string, string> = {}
                const pairs = q.ans.match(/[A-FА-ЕӘ0-9]\s*[-–—:→=]\s*[A-FА-ЕӘ0-9]/gi) || []
                pairs.forEach((p) => {
                    const mm = p.match(/([A-FА-ЕӘ0-9])\s*[-–—:→=]\s*([A-FА-ЕӘ0-9])/i)!
                    const left = /\d/.test(mm[1]) ? mm[1] : lat(mm[1])
                    const right = /\d/.test(mm[2]) ? L[+mm[2] - 1] : lat(mm[2])
                    if (sk.includes(left) && opts.some((o) => o[0] === right)) correct[left] = right
                })
                // номера строк (1, 2) и буквы (A, B) внутри вопроса - только внутренние ключи
                const subsOut = subs.map((r, i) => [L[i], r.t] as [string, string])
                const mapped: Record<string, string> = {}
                subs.forEach((r, i) => {
                    if (correct[r.k as string]) mapped[L[i]] = correct[r.k as string]
                })
                return { type: 'match', text: q.text, hasImage: q.image, subs: subsOut, opts, correct: mapped }
            }
            const opts = q.rows.filter((r) => r.k && !r.num).map((r, i) => [L[i], r.t, r.k, r.mark] as const)
            const letterOf = new Map(opts.map((o) => [o[2], o[0]]))
            const correct = q.ans
                ? Array.from(
                      new Set(
                          (q.ans.match(/[A-FА-ЕӘ](?![а-яәіңғүұқөһa-z])/gi) || []).map(lat),
                      ),
                  )
                      .map((k) => letterOf.get(k))
                      .filter(Boolean) as string[]
                : (opts.filter((o) => o[3]).map((o) => o[0]) as string[])
            const multi = opts.length >= 5 || correct.length > 1
            return {
                type: multi ? 'multi' : 'single',
                text: q.text,
                hasImage: q.image,
                opts: opts.map((o) => [o[0], o[1]] as [string, string]),
                correct,
            }
        })
        .filter((q) => (q.type === 'match' ? q.subs!.length && q.opts.length : q.opts.length))
}
